/**
 * One row of the scope picker's tree, and its children when open (BON-534).
 *
 * A region is selectable in its own right AND expandable to its children, so —
 * like the roster — it carries two sibling controls rather than one nested
 * inside the other. A region with nothing beneath it reserves the caret's width
 * so names stay on one vertical line.
 */
import type { NestedRow } from './nest-rows';
import { SCOPE_ROW_METRICS } from './scope-row-metrics';
import type { ScopeRowVariant } from './scope-row-metrics';
import type { ScopeHrefFor, ScopeRegion } from './scope.types';
import { ScopeCaret } from './ScopeCaret';
import { ScopeMenuLink } from './ScopeMenuLink';

/** Levels after which the indent stops growing. */
const MAX_INDENT_DEPTH = 4;

export interface ScopeMenuRowProps {
  node: NestedRow<ScopeRegion>;
  /** The region currently being viewed; null at all-regions scope. */
  currentRegionId: string | null;
  onToggle: (regionId: string) => void;
  hrefFor: ScopeHrefFor;
  onPick: () => void;
  /** Which regions are open, so the whole tree renders from one map. */
  openIds: ReadonlySet<string>;
  /** Sheet rows are thumb-sized; menu rows are the denser pointer version. */
  variant: ScopeRowVariant;
}

export function ScopeMenuRow({
  node,
  currentRegionId,
  onToggle,
  hrefFor,
  onPick,
  openIds,
  variant,
}: ScopeMenuRowProps) {
  const size = SCOPE_ROW_METRICS[variant];
  const { row } = node;
  const open = openIds.has(row.id);
  const expandable = node.children.length > 0;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          // Indentation stops deepening past a few levels: the hierarchy is
          // recursive, and at depth 5 an ever-growing indent leaves no room for
          // the name. The caret and the tree shape still convey the nesting.
          paddingLeft: Math.min(row.depth, MAX_INDENT_DEPTH) * size.indentStep,
          borderRadius: size.radius,
          background: row.id === currentRegionId ? 'var(--nb-primary-soft)' : 'transparent',
        }}
      >
        {expandable ? (
          <ScopeCaret
            open={open}
            regionName={row.name}
            onToggle={() => onToggle(row.id)}
            width={size.caretWidth}
            size={size.caretIcon}
          />
        ) : (
          <span style={{ flex: `0 0 ${size.caretWidth}px` }} aria-hidden="true" />
        )}
        <ScopeMenuLink
          row={row}
          isHere={row.id === currentRegionId}
          hrefFor={hrefFor}
          onPick={onPick}
          variant={variant}
        />
      </div>
      {open &&
        node.children.map((child) => (
          <ScopeMenuRow
            key={child.row.id}
            node={child}
            currentRegionId={currentRegionId}
            onToggle={onToggle}
            hrefFor={hrefFor}
            onPick={onPick}
            openIds={openIds}
            variant={variant}
          />
        ))}
    </>
  );
}
